import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { EmbedIframe } from "@/components/EmbedIframeExtension";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Link as LinkIcon, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Undo, Redo, Minus, Eraser, Youtube as YoutubeIcon,
} from "lucide-react";
import { useEffect } from "react";

function Btn({ active, onClick, title, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      className="p-2 rounded-sm hover:bg-[color:var(--surface-2)] transition disabled:opacity-30"
      style={{
        background: active ? "var(--accent)" : "transparent",
        color: active ? "#fff" : "var(--text)",
      }}
    >
      {children}
    </button>
  );
}

export default function RichEditor({ value, onChange, placeholder = "Escribí acá...", minHeight = 240 }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] }, link: false, underline: false }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: "noreferrer", target: "_blank" } }),
      Image.configure({ inline: false }),
      Placeholder.configure({ placeholder }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      EmbedIframe,
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm md:prose-base max-w-none focus:outline-none article-body px-4 py-3",
        style: `min-height: ${minHeight}px`,
      },
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return <div className="h-40 bg-[color:var(--surface-2)] animate-pulse rounded-sm" />;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL del link", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt("URL de la imagen");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const addEmbed = () => {
    const url = window.prompt("Pegá la URL de YouTube, Instagram Reel, TikTok o iframe embed");
    if (!url) return;
    editor.chain().focus().setEmbed(url).run();
  };

  return (
    <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--bg)" }} data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-1 p-1.5 border-b" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
        <Btn testId="re-bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Negrita (Ctrl+B)"><Bold size={14} /></Btn>
        <Btn testId="re-italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Itálica"><Italic size={14} /></Btn>
        <Btn testId="re-under" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Subrayado"><UnderlineIcon size={14} /></Btn>
        <Btn testId="re-strike" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} title="Tachado"><Strikethrough size={14} /></Btn>
        <div className="w-px h-5 bg-[color:var(--border)] mx-1" />
        <Btn testId="re-h2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2"><Heading1 size={14} /></Btn>
        <Btn testId="re-h3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3"><Heading2 size={14} /></Btn>
        <Btn testId="re-h4" active={editor.isActive("heading", { level: 4 })} onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()} title="Heading 4"><Heading3 size={14} /></Btn>
        <div className="w-px h-5 bg-[color:var(--border)] mx-1" />
        <Btn testId="re-ul" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista"><List size={14} /></Btn>
        <Btn testId="re-ol" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Lista numerada"><ListOrdered size={14} /></Btn>
        <Btn testId="re-quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Cita"><Quote size={14} /></Btn>
        <Btn testId="re-code" active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Código"><Code size={14} /></Btn>
        <div className="w-px h-5 bg-[color:var(--border)] mx-1" />
        <Btn testId="re-align-left" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Izquierda"><AlignLeft size={14} /></Btn>
        <Btn testId="re-align-center" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Centro"><AlignCenter size={14} /></Btn>
        <Btn testId="re-align-right" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Derecha"><AlignRight size={14} /></Btn>
        <div className="w-px h-5 bg-[color:var(--border)] mx-1" />
        <Btn testId="re-link" active={editor.isActive("link")} onClick={setLink} title="Link"><LinkIcon size={14} /></Btn>
        <Btn testId="re-image" onClick={addImage} title="Insertar imagen"><ImageIcon size={14} /></Btn>
        <Btn testId="re-embed" onClick={addEmbed} title="Insertar video (YouTube/Instagram/TikTok)"><YoutubeIcon size={14} /></Btn>
        <Btn testId="re-hr" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separador"><Minus size={14} /></Btn>
        <Btn testId="re-clear" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Limpiar formato"><Eraser size={14} /></Btn>
        <div className="ml-auto flex">
          <Btn testId="re-undo" onClick={() => editor.chain().focus().undo().run()} title="Deshacer"><Undo size={14} /></Btn>
          <Btn testId="re-redo" onClick={() => editor.chain().focus().redo().run()} title="Rehacer"><Redo size={14} /></Btn>
        </div>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
